import { ColumnDef } from '@tanstack/react-table';
import { Check, ChevronRight, Copy } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { TableColumnHeader } from '@/components/orderList/TableColumnHeader';
import { RESERVATION_TONE, reservationSummary } from '@/utils/reservation';

export type TInvestment = {
  id: string;
  customer: {
    id: string;
    name: string;
    email: string;
    status?: string;
  };
  asset: {
    assetId: string;
    name: string;
    type: string;
    /** Network the tokens were delivered on, e.g. AVALANCHE_FUJI. */
    chain?: string | null;
  };
  totalAmount: number;
  projectedNumberOfTokens?: number;
  tokensAllocated?: number | null;
  status: string;
  isCrypto: boolean;
  createdAt: string;
  updatedAt?: string | null;
  completedAt?: string | null;
  paymentReference?: string | null;
  transactionHash?: string | null;
  /** Explorer link for transactionHash, built by the API on the settlement chain. */
  explorerUrl?: string | null;
  /** The investor's active wallet address. */
  customerWalletAddress?: string | null;
  /** 5-day token reservation: ACTIVE | SECURED | RELEASED, or null when never reserved. */
  reservationStatus?: string | null;
  reservedAt?: string | null;
  reservationExpiresAt?: string | null;
  reservationSecuredAt?: string | null;
  reservationReleasedAt?: string | null;
  /** Subscription agreement envelope. */
  docusignEnvelopeId?: string | null;
  /** The investor's accredited-investor letter envelope (one per investor). */
  accreditationEnvelopeId?: string | null;
  /** NOT_STARTED | SENT | SIGNED | DECLINED | VOIDED | REJECTED */
  accreditationStatus?: string | null;
  /** ACE accredited-investor credential: null | ISSUING | PENDING | ACTIVE | FAILED */
  accreditationAceStatus?: string | null;
  /** null (not reviewed) | APPROVED | DENIED */
  accreditationReviewStatus?: string | null;
  /** InvestmentRecordRegistry write: null | SENDING | PENDING | REGISTERED | FAILED */
  registryStatus?: string | null;
  registryTxHash?: string | null;
  /**
   * Whether a mint would succeed right now. Computed server-side per investment.
   * Null on older responses; treat that as "unknown", never as "blocked".
   */
  compliance?: {
    ready: boolean;
    blockedStatus?: string | null;
    reason?: string | null;
    applicable: boolean;
  } | null;
};

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
  COMPLETED: 'bg-blue-50 text-blue-700 border-blue-200',
  CANCELLED: 'bg-rose-50 text-rose-700 border-rose-200',
  // Amber = resolves itself, rose = needs a human. The whole point of splitting
  // these statuses is that they call for different actions.
  COMPLIANCE_PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
  COMPLIANCE_BLOCKED: 'bg-rose-50 text-rose-700 border-rose-200',
  ASSET_NOT_READY: 'bg-rose-50 text-rose-700 border-rose-200',
  MINT_FAILED: 'bg-rose-50 text-rose-700 border-rose-200',
};

export const InvestmentStatusPill = ({ status }: { status?: string }) => {
  const normalized = (status ?? '').toUpperCase();
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium ${
        STATUS_COLORS[normalized] ||
        'bg-slate-50 text-slate-700 border-slate-200'
      }`}
    >
      {normalized || 'UNKNOWN'}
    </span>
  );
};

/** A long ID or address, shortened, that copies the full value on click. */
export const CopyValue = ({
  value,
  head = 8,
  tail = 0,
}: {
  value?: string | null;
  head?: number;
  tail?: number;
}) => {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="text-xs text-muted-foreground">-</span>;
  const short =
    value.length > head + tail + 1
      ? `${value.slice(0, head)}…${tail ? value.slice(-tail) : ''}`
      : value;

  const copy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked; the full value is in the tooltip */
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      title={value}
      className="inline-flex items-center gap-1 font-mono text-xs text-muted-foreground hover:text-foreground"
    >
      {short}
      {copied ? (
        <Check className="h-3 w-3 text-emerald-600" />
      ) : (
        <Copy className="h-3 w-3 opacity-60" />
      )}
    </button>
  );
};

const OpenInvestment = ({ investment }: { investment: TInvestment }) => {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() =>
        navigate(`/admin/investments/${investment.id}`, {
          state: { investment },
        })
      }
      className="rounded-md p-1 text-muted-foreground hover:bg-slate-100 hover:text-foreground"
      title="Open investment details"
      aria-label={`Open investment ${investment.id}`}
    >
      <ChevronRight className="h-4 w-4" />
    </button>
  );
};

/**
 * The investments list: just enough to find one. Everything else (amount, asset, mint
 * readiness, documents, accreditation, payment actions) is on the investment page, opened
 * from the row's arrow.
 */
export const InvestmentsTableColumns: ColumnDef<TInvestment>[] = [
  {
    accessorKey: 'status',
    header: ({ column }) => (
      <TableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      const reservation = reservationSummary(row.original);
      return (
        <div className="flex flex-col items-start gap-1">
          <InvestmentStatusPill status={row.original.status} />
          {reservation && (
            <span
              className={`inline-flex whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-medium ${RESERVATION_TONE[reservation.tone]}`}
              title={reservation.detail}
            >
              {reservation.label}
            </span>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: 'id',
    header: ({ column }) => (
      <TableColumnHeader column={column} title="Investment ID" />
    ),
    cell: ({ row }) => <CopyValue value={row.original.id} />,
    enableSorting: false,
    enableHiding: false,
  },
  {
    id: 'customerEmail',
    header: ({ column }) => (
      <TableColumnHeader column={column} title="Customer" />
    ),
    cell: ({ row }) => (
      <span className="text-sm">{row.original.customer?.email ?? '-'}</span>
    ),
    enableSorting: false,
  },
  {
    id: 'customerWallet',
    header: ({ column }) => (
      <TableColumnHeader column={column} title="Wallet" />
    ),
    cell: ({ row }) => (
      <CopyValue value={row.original.customerWalletAddress} head={8} tail={6} />
    ),
    enableSorting: false,
  },
  {
    id: 'open',
    header: () => <span className="sr-only">Open</span>,
    cell: ({ row }) => <OpenInvestment investment={row.original} />,
    enableSorting: false,
    enableHiding: false,
  },
];
